import * as RadixColors from '@radix-ui/colors';
import { generatePalette } from './generative-theme';
import { createDelugeTheme, createPaletteFromRadixPalette, } from './theme-create';
export const resolvePalette = (theme, color) => theme.palette[color];
const grayDark = createPaletteFromRadixPalette(RadixColors.grayDark);
const grayDarkA = createPaletteFromRadixPalette(RadixColors.grayDarkA);
const blueDark = createPaletteFromRadixPalette(RadixColors.indigoDark);
const blueDarkA = createPaletteFromRadixPalette(RadixColors.indigoDarkA);
/**
 * The kol.trade accent: a warm yellow on black.
 *
 * One colour, with the other eleven steps of its scale derived from it the
 * same way the Radix scales are, so hover, border and tint steps sit in the
 * same family. Text on it is dark — see `contrastTextOn`.
 */
export const KOL_YELLOW = '#FFD23F';
const { primary: kolYellowDark, primaryA: kolYellowDarkA } = generatePalette({
    appearance: 'dark',
    appearanceColor: '#000000',
    primary: KOL_YELLOW,
});
const orangeDark = createPaletteFromRadixPalette(RadixColors.orangeDark);
const orangeDarkA = createPaletteFromRadixPalette(RadixColors.orangeDarkA);
const greenDark = createPaletteFromRadixPalette(RadixColors.greenDark);
const greenDarkA = createPaletteFromRadixPalette(RadixColors.greenDarkA);
const rubyDark = createPaletteFromRadixPalette(RadixColors.rubyDark);
const rubyDarkA = createPaletteFromRadixPalette(RadixColors.rubyDarkA);
const amberDark = createPaletteFromRadixPalette(RadixColors.amberDark);
const amberDarkA = createPaletteFromRadixPalette(RadixColors.amberDarkA);
const cyanDark = createPaletteFromRadixPalette(RadixColors.cyanDark);
const cyanDarkA = createPaletteFromRadixPalette(RadixColors.cyanDarkA);
const purpleDark = createPaletteFromRadixPalette(RadixColors.purpleDark);
const purpleDarkA = createPaletteFromRadixPalette(RadixColors.purpleDarkA);
const pinkDark = createPaletteFromRadixPalette(RadixColors.pinkDark);
const pinkDarkA = createPaletteFromRadixPalette(RadixColors.pinkDarkA);
const yellowDark = createPaletteFromRadixPalette(RadixColors.yellowDark);
const candleGreenDark = {
    [1]: '#000000',
    [2]: '#0A1512',
    [3]: '#072A23',
    [4]: '#02392F',
    [5]: '#08473C',
    [6]: '#165649',
    [7]: '#216759',
    [8]: '#277E6C',
    [9]: '#039881',
    [10]: '#01C3A5',
    [11]: '#5CD1B7',
    [12]: '#A8F0DE',
};
const candleGreenDarkA = {
    [1]: '#0398810D',
    [2]: '#0398811A',
    [3]: '#2BFFD529',
    [4]: '#09FFD338',
    [5]: '#1DFFD845',
    [6]: '#42FFD954',
    [7]: '#52FFDD66',
    [8]: '#4FFFDB7D',
    [9]: '#05FFD999',
    [10]: '#5FFFDDA6',
    [11]: '#70FFDFD1',
    [12]: '#B3FFECF0',
};
const candleRedDark = {
    [1]: '#000000',
    [2]: '#1A0C0B',
    [3]: '#390B0D',
    [4]: '#52040D',
    [5]: '#650B15',
    [6]: '#771B21',
    [7]: '#922C30',
    [8]: '#BD3B40',
    [9]: '#EE3544',
    [10]: '#DF2138',
    [11]: '#FF8F8B',
    [12]: '#FFD0CD',
};
const candleRedDarkA = {
    [1]: '#FF43391A',
    [2]: '#B80D0F1A',
    [3]: '#FF323B38',
    [4]: '#FF433952',
    [5]: '#FF1C3666',
    [6]: '#FF3A4773',
    [7]: '#FF4D5491',
    [8]: '#FF5057BD',
    [9]: '#FF3949CC',
    [10]: '#FF2640D9',
    [11]: '#FF8F8BE5',
    [12]: '#FFD0CDF2',
};
const solanaDark = {
    [1]: '#100E21',
    [2]: '#17142C',
    [3]: '#261A53',
    [4]: '#321A72',
    [5]: '#3B2382',
    [6]: '#452E92',
    [7]: '#533BAA',
    [8]: '#6649CF',
    [9]: '#7337FF',
    [10]: '#671FEE',
    [11]: '#B2A7FF',
    [12]: '#DFDCFF',
};
const tonDark = {
    [1]: '#09121A',
    [2]: '#0F1A23',
    [3]: '#092940',
    [4]: '#003558',
    [5]: '#004269',
    [6]: '#0D507B',
    [7]: '#1B6191',
    [8]: '#2277B0',
    [9]: '#0098E9',
    [10]: '#008BDB',
    [11]: '#6DBEFC',
    [12]: '#C5E5FF',
};
const customGreenDark = {
    [1]: '#0E1512',
    [2]: '#121B17',
    [3]: '#132D21',
    [4]: '#113B29',
    [5]: '#174933',
    [6]: '#20573E',
    [7]: '#28684A',
    [8]: '#2F7C57',
    [9]: '#00CA75',
    [10]: '#33B074',
    [11]: '#3DD68C',
    [12]: '#B1F1CB',
};
const customGreenDarkA = {
    [1]: '#00DE4505',
    [2]: '#29F99D0A',
    [3]: '#22FF991E',
    [4]: '#11FF992D',
    [5]: '#2BFFA23C',
    [6]: '#44FFAA4B',
    [7]: '#50FDAC5E',
    [8]: '#54FFAD73',
    [9]: '#44FFA49E',
    [10]: '#43FEA4AB',
    [11]: '#46FEA5D4',
    [12]: '#BBFFD7F0',
};
const premiumDark = {
    [1]: '#110E1F',
    [2]: '#181529',
    [3]: '#261D4C',
    [4]: '#322067',
    [5]: '#3B2975',
    [6]: '#453484',
    [7]: '#54419B',
    [8]: '#6851BF',
    [9]: '#8A68FF',
    [10]: '#7E5AF1',
    [11]: '#B5A6FF',
    [12]: '#E0DCFF',
};
const acidDark = {
    [1]: '#000000',
    [2]: '#0A1507',
    [3]: '#12290C',
    [4]: '#17390F',
    [5]: '#1C4911',
    [6]: '#225814',
    [7]: '#286A17',
    [8]: '#2D7E15',
    [9]: '#52FF00',
    [10]: '#59F229',
    [11]: '#5EF630',
    [12]: '#5EF630',
};
const downAccentDark = {
    [1]: '#1f0f14',
    [2]: '#2a1118',
    [3]: '#3d1520',
    [4]: '#551a29',
    [5]: '#6e1f32',
    [6]: '#88243a',
    [7]: '#a52a43',
    [8]: '#c7304b',
    [9]: '#ff113c',
    [10]: '#ff4a6a',
    [11]: '#ff9bb0',
    [12]: '#ffd6df',
};
const upAccentDark = {
    [1]: '#0f1f12',
    [2]: '#112a16',
    [3]: '#153d1f',
    [4]: '#1a5528',
    [5]: '#1f6e31',
    [6]: '#24883a',
    [7]: '#2aa543',
    [8]: '#30c74d',
    [9]: '#00b112',
    [10]: '#3fdc5a',
    [11]: '#9bf0a8',
    [12]: '#d6fbe0',
};
const upAccentDarkA = {
    [1]: '#00000000',
    [2]: '#99ff8d14',
    [3]: '#80ff7a28',
    [4]: '#58ff543a',
    [5]: '#5aff574a',
    [6]: '#5bff585a',
    [7]: '#59ff566b',
    [8]: '#53ff517e',
    [9]: '#00ff1ab1',
    [10]: '#00ff00a4',
    [11]: '#5fff5dd8',
    [12]: '#baffb5f6',
};
const downAccentDarkA = {
    [1]: '#00000000',
    [2]: '#ff68681b',
    [3]: '#ff1a2a3d',
    [4]: '#ff001857',
    [5]: '#ff00256a',
    [6]: '#ff19377e',
    [7]: '#ff364699',
    [8]: '#ff3848c6',
    [9]: '#ff113c',
    [10]: '#ff0032f0',
    [11]: '#ff8f8c',
    [12]: '#ffceca',
};
const goldenDark = {
    [1]: '#13110b',
    [2]: '#1c180f',
    [3]: '#2c2207',
    [4]: '#3b2a00',
    [5]: '#473400',
    [6]: '#554200',
    [7]: '#685415',
    [8]: '#836a1e',
    [9]: '#ffcb01',
    [10]: '#f5c100',
    [11]: '#ffce11',
    [12]: '#fde8b3',
};
const goldenDarkA = {
    [1]: '#bb110003',
    [2]: '#fba6000c',
    [3]: '#ffa7001d',
    [4]: '#ff9f002d',
    [5]: '#ffab003a',
    [6]: '#ffbd0049',
    [7]: '#fdc71c5e',
    [8]: '#fdca2c7b',
    [9]: '#ffcb01',
    [10]: '#fec800f5',
    [11]: '#ffce11',
    [12]: '#ffeab4fd',
};
const silverDark = {
    [1]: '#111111',
    [2]: '#191919',
    [3]: '#222222',
    [4]: '#2a2a2a',
    [5]: '#313131',
    [6]: '#3b3b3b',
    [7]: '#494949',
    [8]: '#616161',
    [9]: '#c1c1c1',
    [10]: '#b7b7b7',
    [11]: '#b5b5b5',
    [12]: '#ececec',
};
const silverDarkA = {
    [1]: '#00000000',
    [2]: '#ffffff09',
    [3]: '#ffffff12',
    [4]: '#ffffff1b',
    [5]: '#ffffff22',
    [6]: '#ffffff2d',
    [7]: '#ffffff3c',
    [8]: '#ffffff56',
    [9]: '#ffffffbd',
    [10]: '#ffffffb2',
    [11]: '#ffffffb0',
    [12]: '#ffffffeb',
};
const bronzeDark = {
    [1]: '#14100e',
    [2]: '#1d1714',
    [3]: '#2f1e14',
    [4]: '#402313',
    [5]: '#4d2b18',
    [6]: '#5c3622',
    [7]: '#72452e',
    [8]: '#93593b',
    [9]: '#e6986f',
    [10]: '#da8d64',
    [11]: '#f4a57c',
    [12]: '#f7dacc',
};
const bronzeDarkA = {
    [1]: '#d1000004',
    [2]: '#fd874c0d',
    [3]: '#f9762921',
    [4]: '#fc6b1b33',
    [5]: '#fd772d41',
    [6]: '#fd854651',
    [7]: '#ff915968',
    [8]: '#fe945d8c',
    [9]: '#fea779e5',
    [10]: '#fea373d8',
    [11]: '#feab81f4',
    [12]: '#fee0d2f7',
};
const boostGradientFromDark = {
    [1]: '#10120a',
    [2]: '#17190e',
    [3]: '#23270f',
    [4]: '#2d330c',
    [5]: '#38400a',
    [6]: '#454d12',
    [7]: '#545e1c',
    [8]: '#677321',
    [9]: '#e4ff34',
    [10]: '#dbf520',
    [11]: '#cfe63e',
    [12]: '#e7f6af',
};
const boostGradientFromDarkA = {
    [1]: '#00910002',
    [2]: '#bcf40009',
    [3]: '#d1fb0018',
    [4]: '#d2fb0025',
    [5]: '#d4fc0033',
    [6]: '#ddfd1541',
    [7]: '#dffe3353',
    [8]: '#e2ff3869',
    [9]: '#e4ff34',
    [10]: '#e3fe20f5',
    [11]: '#e4fe43e5',
    [12]: '#eefeb4f6',
};
const boostGradientToDark = {
    [1]: '#0b140b',
    [2]: '#0f1c0f',
    [3]: '#112d13',
    [4]: '#113c16',
    [5]: '#134b1a',
    [6]: '#155a1f',
    [7]: '#186c24',
    [8]: '#158029',
    [9]: '#06ff4d',
    [10]: '#1bf34d',
    [11]: '#21f54f',
    [12]: '#b0ffb3',
};
const boostGradientToDarkA = {
    [1]: '#00d10004',
    [2]: '#00fb000c',
    [3]: '#11ff221e',
    [4]: '#11fb2d2f',
    [5]: '#1afc363f',
    [6]: '#1efd3e4f',
    [7]: '#24fe4362',
    [8]: '#1aff4577',
    [9]: '#06ff4d',
    [10]: '#1bfe50f3',
    [11]: '#21fe51f5',
    [12]: '#b0ffb3',
};
const boostIconDark = {
    [1]: '#0b140c',
    [2]: '#121b13',
    [3]: '#152b19',
    [4]: '#0f3d1b',
    [5]: '#124b22',
    [6]: '#155b29',
    [7]: '#166c30',
    [8]: '#127f37',
    [9]: '#1a6f33',
    [10]: '#185e2c',
    [11]: '#70d083',
    [12]: '#acf7b8',
};
const boostIconDarkA = {
    [1]: '#00d10004',
    [2]: '#29f9400b',
    [3]: '#36fe5a1c',
    [4]: '#07fb4730',
    [5]: '#16fc563f',
    [6]: '#1efd5e50',
    [7]: '#1ffe6262',
    [8]: '#14ff6476',
    [9]: '#28ff6765',
    [10]: '#27fe6453',
    [11]: '#88ff9fcd',
    [12]: '#b1febdf7',
};
const boostGlowDark = {
    [1]: '#0b140b',
    [2]: '#0f1c0f',
    [3]: '#112d13',
    [4]: '#113c16',
    [5]: '#124b1a',
    [6]: '#155a1f',
    [7]: '#176c24',
    [8]: '#148029',
    [9]: '#00ff4d',
    [10]: '#18f34d',
    [11]: '#1ef54f',
    [12]: '#b0ffb3',
};
const boostGlowDarkA = {
    [1]: '#00d10004',
    [2]: '#00fb000c',
    [3]: '#11ff221e',
    [4]: '#11fb2d2f',
    [5]: '#16fc363f',
    [6]: '#1efd3e4f',
    [7]: '#21fe4362',
    [8]: '#18ff4577',
    [9]: '#00ff4d',
    [10]: '#18fe50f3',
    [11]: '#1efe51f5',
    [12]: '#b0ffb3',
};
const boostButtonDark = {
    [1]: '#0c140a',
    [2]: '#101b0e',
    [3]: '#152c10',
    [4]: '#193b11',
    [5]: '#1e4a13',
    [6]: '#235915',
    [7]: '#286a17',
    [8]: '#2d7e16',
    [9]: '#52ff00',
    [10]: '#59f229',
    [11]: '#5ef630',
    [12]: '#bbffab',
};
const boostButtonDarkA = {
    [1]: '#00d10004',
    [2]: '#00f9000b',
    [3]: '#35ff091d',
    [4]: '#3fff112d',
    [5]: '#47fc1a3e',
    [6]: '#4cfd1f4e',
    [7]: '#4efd2160',
    [8]: '#4fff1c75',
    [9]: '#52ff00',
    [10]: '#5dfe2af2',
    [11]: '#60fe31f6',
    [12]: '#bbffab',
};
const subscriptionGradientFromDark = {
    [1]: '#120f1b',
    [2]: '#191525',
    [3]: '#291e43',
    [4]: '#35245a',
    [5]: '#3f2c67',
    [6]: '#4a3775',
    [7]: '#59458a',
    [8]: '#6f55aa',
    [9]: '#b59bfb',
    [10]: '#aa90ef',
    [11]: '#bea4ff',
    [12]: '#e4dcfe',
};
const subscriptionGradientFromDarkA = {
    [1]: '#2900f90b',
    [2]: '#6e3ff916',
    [3]: '#834ffe36',
    [4]: '#854ffd4f',
    [5]: '#8f5bfd5d',
    [6]: '#986bfd6c',
    [7]: '#9f77ff82',
    [8]: '#a47bffa4',
    [9]: '#b89dfffb',
    [10]: '#b599ffee',
    [11]: '#bea4ff',
    [12]: '#e5ddfffe',
};
const subscriptionGradientToDark = {
    [1]: '#0d101d',
    [2]: '#131627',
    [3]: '#1c224c',
    [4]: '#242a68',
    [5]: '#2d337a',
    [6]: '#363e8a',
    [7]: '#414a9f',
    [8]: '#4d57bb',
    [9]: '#6977fd',
    [10]: '#5e6aef',
    [11]: '#9dafff',
    [12]: '#d8e0ff',
};
const subscriptionGradientToDarkA = {
    [1]: '#0000fd0d',
    [2]: '#2747fb18',
    [3]: '#3d55fd40',
    [4]: '#4555fd5e',
    [5]: '#515efe71',
    [6]: '#5a6aff82',
    [7]: '#6170fe99',
    [8]: '#6572feb7',
    [9]: '#6a78fffd',
    [10]: '#6471ffee',
    [11]: '#9dafff',
    [12]: '#d8e0ff',
};
const subscriptionIconDark = {
    [1]: '#0d101c',
    [2]: '#121627',
    [3]: '#1b224c',
    [4]: '#22296a',
    [5]: '#2a337b',
    [6]: '#333e8a',
    [7]: '#3e4b9e',
    [8]: '#4a59bc',
    [9]: '#4f5fd7',
    [10]: '#4756b8',
    [11]: '#9aafff',
    [12]: '#d8e1ff',
};
const subscriptionIconDarkA = {
    [1]: '#0000fb0c',
    [2]: '#1c47fb18',
    [3]: '#3955fd40',
    [4]: '#3e51fd60',
    [5]: '#495eff72',
    [6]: '#546aff82',
    [7]: '#5d72fe98',
    [8]: '#6075feb8',
    [9]: '#5b6efed5',
    [10]: '#5e74ffb3',
    [11]: '#9aafff',
    [12]: '#d8e1ff',
};
const subscriptionGlowDark = {
    [1]: '#100f1d',
    [2]: '#161528',
    [3]: '#241e4e',
    [4]: '#2f216d',
    [5]: '#392a7d',
    [6]: '#43358c',
    [7]: '#5042a2',
    [8]: '#604fc3',
    [9]: '#7e66ff',
    [10]: '#7358f0',
    [11]: '#aea8ff',
    [12]: '#dedeff',
};
const subscriptionGlowDarkA = {
    [1]: '#0000fd0d',
    [2]: '#443afc19',
    [3]: '#5a43fd42',
    [4]: '#5f3bfe63',
    [5]: '#6948ff74',
    [6]: '#7257ff84',
    [7]: '#7861fe9c',
    [8]: '#7b64ffbf',
    [9]: '#7e66ff',
    [10]: '#7a5dffef',
    [11]: '#aea8ff',
    [12]: '#dedeff',
};
const subscriptionButtonDark = {
    [1]: '#110e1f',
    [2]: '#181529',
    [3]: '#261d4c',
    [4]: '#322067',
    [5]: '#3b2975',
    [6]: '#453484',
    [7]: '#54419b',
    [8]: '#6851bf',
    [9]: '#8a68ff',
    [10]: '#7e5af1',
    [11]: '#b5a6ff',
    [12]: '#e0dcff',
};
const subscriptionButtonDarkA = {
    [1]: '#1100ff0f',
    [2]: '#5639fd1a',
    [3]: '#6541fd40',
    [4]: '#6c3afd5d',
    [5]: '#744afd6c',
    [6]: '#7c59fd7c',
    [7]: '#8564ff94',
    [8]: '#8768febb',
    [9]: '#8a68ff',
    [10]: '#855ffff0',
    [11]: '#b5a6ff',
    [12]: '#e0dcff',
};
export const darkPalette = {
    // colors
    gray: grayDark,
    grayA: grayDarkA,
    blue: blueDark,
    blueA: blueDarkA,
    red: rubyDark,
    ruby: rubyDark,
    rubyA: rubyDarkA,
    green: customGreenDark,
    greenA: customGreenDarkA,
    orange: orangeDark,
    amber: amberDark,
    amberA: amberDarkA,
    acid: acidDark,
    cyan: cyanDark,
    cyanA: cyanDarkA,
    purple: purpleDark,
    pink: pinkDark,
    yellow: yellowDark,
    golden: goldenDark,
    goldenA: goldenDarkA,
    silver: silverDark,
    silverA: silverDarkA,
    bronze: bronzeDark,
    bronzeA: bronzeDarkA,
    // boost
    boostGradientFrom: boostGradientFromDark,
    boostGradientFromA: boostGradientFromDarkA,
    boostGradientTo: boostGradientToDark,
    boostGradientToA: boostGradientToDarkA,
    boostIcon: boostIconDark,
    boostIconA: boostIconDarkA,
    boostGlow: boostGlowDark,
    boostGlowA: boostGlowDarkA,
    boostButton: boostButtonDark,
    boostButtonA: boostButtonDarkA,
    // subscription
    subscriptionGradientFrom: subscriptionGradientFromDark,
    subscriptionGradientFromA: subscriptionGradientFromDarkA,
    subscriptionGradientTo: subscriptionGradientToDark,
    subscriptionGradientToA: subscriptionGradientToDarkA,
    subscriptionIcon: subscriptionIconDark,
    subscriptionIconA: subscriptionIconDarkA,
    subscriptionGlow: subscriptionGlowDark,
    subscriptionGlowA: subscriptionGlowDarkA,
    subscriptionButton: subscriptionButtonDark,
    subscriptionButtonA: subscriptionButtonDarkA,
    // content
    text: grayDark,
    textA: grayDarkA,
    background: grayDark,
    backgroundA: grayDarkA,
    // intent
    warning: amberDark,
    warningA: amberDarkA,
    warningAccent: orangeDark,
    warningAccentA: orangeDarkA,
    success: greenDark,
    successA: greenDarkA,
    successAccent: acidDark,
    successAccentA: acidDark,
    error: rubyDark,
    errorAccent: downAccentDark,
    errorAccentA: downAccentDarkA,
    errorA: rubyDarkA,
    // accent
    primary: kolYellowDark,
    primaryA: kolYellowDarkA,
    premium: premiumDark,
    // candle
    candleGreen: candleGreenDark,
    candleGreenBorder: candleGreenDark,
    candleGreenWick: candleGreenDark,
    candleGreenA: candleGreenDarkA,
    candleGreenBorderA: candleGreenDarkA,
    candleGreenWickA: candleGreenDarkA,
    candleRed: candleRedDark,
    candleRedBorder: candleRedDark,
    candleRedWick: candleRedDark,
    candleRedA: candleRedDarkA,
    candleRedBorderA: candleRedDarkA,
    candleRedWickA: candleRedDarkA,
    //trend
    neutral: grayDark,
    neutralA: grayDarkA,
    neutralAccent: grayDark,
    neutralAccentA: grayDarkA,
    up: candleGreenDark,
    upA: candleGreenDarkA,
    upAccent: upAccentDark,
    upAccentA: upAccentDarkA,
    down: candleRedDark,
    downA: candleRedDarkA,
    downAccent: downAccentDark,
    downAccentA: downAccentDarkA,
    // currency
    solana: solanaDark,
    ton: tonDark,
    ethereum: blueDark,
    base: blueDark,
    bsc: amberDark,
};
const colors = {
    black: '#000000',
    white: '#FFFFFF',
};
const transition = {
    default: '.15s linear',
};
export const DELUGE_DARK_THEME = {
    appearance: 'dark',
    appearanceColor: '#111111',
    palette: darkPalette,
    colors,
    fontWeight: {
        medium: '500',
        semibold: '600',
        bold: '700',
    },
    fontSize: {
        xxs: '10px',
        xs: '12px',
        s: '14px',
        m: '16px',
        l: '20px',
    },
    transition,
    fontFamily: `"Inter", sans-serif;`,
    color: {
        primary: 'gray',
        secondary: 'blue',
        success: 'blue',
        error: 'blue',
        info: 'blue',
        warning: 'blue',
        white: {
            4: 'rgba(255, 255, 255, 0.04)',
            5: 'rgba(255, 255, 255, 0.05)',
            6: 'rgba(255, 255, 255, 0.06)',
            7: 'rgba(255, 255, 255, 0.07)',
            10: 'rgba(255, 255, 255, 0.1)',
            11: 'rgba(255, 255, 255, 0.11)',
            13: 'rgba(255, 255, 255, 0.13)',
            15: 'rgba(255, 255, 255, 0.15)',
            17: 'rgba(255, 255, 255, 0.17)',
            20: 'rgba(255, 255, 255, 0.2)',
            25: 'rgba(255, 255, 255, 0.25)',
            50: 'rgba(255, 255, 255, 0.5)',
            60: 'rgba(255, 255, 255, 0.6)',
            70: 'rgba(255, 255, 255, 0.7)',
            80: 'rgba(255, 255, 255, 0.8)',
            90: 'rgba(255, 255, 255, 0.9)',
            100: '#FFFFFF',
        },
        black: {
            10: 'rgba(0, 0, 0, 0.10)',
            30: 'rgba(0, 0, 0, 0.30)',
            50: 'rgba(0, 0, 0, 0.50)',
            100: '#000',
        },
        black2: {
            30: 'rgba(15, 15, 26, 0.3)',
            50: 'rgba(15, 15, 26, 0.5)',
            60: 'rgba(15, 15, 26, 0.6)',
            80: 'rgba(15, 15, 26, 0.8)',
            100: '#15151A',
        },
        black3: {
            50: 'rgba(28, 32, 45, 0.5)',
            100: '#1C202D',
        },
        black4: '#2A2E3E',
        black5: '#1D1F24',
        gray: '#4B5060',
        gray2: {
            25: 'rgba(103, 108, 125, 0.25)',
            50: 'rgba(103, 108, 125, 0.5)',
            100: '#676C7D',
        },
        gray3: '#A6ADB8',
        accent1: '#E694FF',
        accent2: '#62B6FF',
        accent3: '#5F4775',
        accent4: '#FFAE00',
        accent5: {
            100: '#FF1F1F',
            20: 'rgba(255, 31, 31, 0.2)',
            25: 'rgba(255, 31, 31, 0.25)',
        },
        accent6: {
            100: '#04D900',
            20: 'rgba(4, 217, 0, 0.2)',
            25: 'rgba(4, 217, 0, 0.25)',
        },
        accent7: '#53C1FF',
        gradient1: 'linear-gradient(135deg, #FF53E8 0%, #6600FF 50%, #39216A 100%)',
        gradient2: 'linear-gradient(135deg, #5643FF 0%, #E170FF 100%)',
        gradient3: 'radial-gradient(57.56% 402.91% at 50% -115.91%, #A0FF53 0%, #00D567 41.6%, #1D8570 100%)',
        gradient4: 'radial-gradient(57.56% 402.91% at 50% -115.91%, #FF53FF 0%, #CD4A33 50%, #51005B 100%)',
        gradient5: 'linear-gradient(90deg, #53C1FF 0%, #0066FF 100%)',
        gradient6: 'radial-gradient(158% 158% at 50% -58%, rgba(66, 174, 255, 0.5) 0%, rgba(0, 81, 171, 0) 100%), #15151a',
    },
    shadows: {
        1: '0px 0px 32px 0px #0000003D',
    },
    background: {
        gradient1: 'radial-gradient(578.9% 578.9% at -14.17% -346.43%, #4D495C 0%, rgba(77, 73, 92, 0.00) 100%), rgba(21, 21, 26, 0.80)',
    },
    mobile: {
        queryMaxWidth: '(max-width: 767px)',
        width: 767,
    },
    tablet: {
        queryMinWidth: '(min-width: 768px)',
        queryMaxWidth: '(max-width: 1080px)',
        width: 1080,
    },
    desktop: {
        queryMinWidth: '(min-width: 1440px)',
        queryMaxWidth: '(max-width: 1440px)',
        width: 1440,
    },
    desktopLarge: {
        queryMinWidth: '(min-width: 1800px)',
        queryMaxWidth: '(max-width: 1800px)',
        width: 1800,
    },
    desktopSmall: {
        queryMaxWidth: '(max-width: 1280px)',
        width: 1280,
    },
    resolvePalette: resolvePalette,
};
export const figmaColors = {
    uptrend: '#52FF00',
    downtrend: '#FF4034',
    grayInactive: '#C3C3C3',
    gray2: '#707070',
    gray3: '#ABAEB5',
    //yellow
    yellow: '#FFAE00',
    //green
    green1: '#00b112',
    green2: '#1d2b26',
    //blue
    blue: '#3E63FF',
    blue1: '#253b99',
    //red
    red1: '#B63641',
    red2: '#2a1819',
};
// export const premiumColors = {
//   boosters: {
//     glow: '#00ff4d2d',
//     icon: '#1A6F33',
//     blur: '#6bff4275',
//     gradientFrom: '#E4FF34',
//     gradientTo: '#06FF4D',
//     button: '#52ff00',
//   },
//   subscription: {
//     glow: '#7e66ff2d',
//     icon: '#2C367E',
//     blur: '#b59bfb75',
//     gradientFrom: '#b59bfb',
//     gradientTo: '#6977FD',
//     button: '#8a68ff',
//   },
// }
export const uiKitDelugeTheme = createDelugeTheme({
    appearance: 'dark',
    appearanceColor: '#111111',
    palette: darkPalette,
    fontFamily: `"Inter", sans-serif;`,
});
export const uiKitTestTheme = createDelugeTheme({
    appearance: 'dark',
    appearanceColor: '#111111',
    palette: {
        ...darkPalette,
        gray: cyanDark,
        grayA: cyanDarkA,
        text: cyanDark,
        textA: cyanDarkA,
        background: cyanDark,
        backgroundA: cyanDarkA,
        primary: rubyDark,
        primaryA: rubyDarkA,
        candleGreen: amberDark,
        candleGreenA: amberDarkA,
        up: amberDark,
        upA: amberDarkA,
        upAccent: orangeDark,
        upAccentA: orangeDarkA,
        candleRed: blueDark,
        candleRedA: blueDarkA,
        down: blueDark,
        downA: blueDarkA,
        downAccent: purpleDark,
        downAccentA: purpleDarkA,
        neutral: cyanDark,
        neutralA: cyanDarkA,
        neutralAccent: cyanDark,
        neutralAccentA: cyanDarkA,
        success: customGreenDark,
        successA: customGreenDarkA,
        error: rubyDark,
        errorA: rubyDarkA,
        boostGradientFrom: purpleDark,
        boostGradientFromA: purpleDarkA,
        boostGradientTo: pinkDark,
        boostGradientToA: pinkDarkA,
        boostIcon: pinkDark,
        boostIconA: pinkDarkA,
        boostGlow: purpleDark,
        boostGlowA: purpleDarkA,
        boostButton: pinkDark,
        boostButtonA: pinkDarkA,
        subscriptionGradientFrom: amberDark,
        subscriptionGradientFromA: amberDarkA,
        subscriptionGradientTo: orangeDark,
        subscriptionGradientToA: orangeDarkA,
        subscriptionIcon: orangeDark,
        subscriptionIconA: orangeDarkA,
        subscriptionGlow: amberDark,
        subscriptionGlowA: amberDarkA,
        subscriptionButton: orangeDark,
        subscriptionButtonA: orangeDarkA,
    },
    fontFamily: `"Inter", sans-serif;`,
});
//# sourceMappingURL=theme-palette.js.map